package diana.soleil.design.iraniancanadienbazzar.navigation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import diana.soleil.design.iraniancanadienbazzar.auth.AuthViewModel
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import iraniancanadienbazzar.shared.generated.resources.Res
import iraniancanadienbazzar.shared.generated.resources.delete_account_cancel
import iraniancanadienbazzar.shared.generated.resources.delete_account_confirm
import iraniancanadienbazzar.shared.generated.resources.delete_account_failed
import iraniancanadienbazzar.shared.generated.resources.delete_account_password_label
import iraniancanadienbazzar.shared.generated.resources.delete_account_title
import iraniancanadienbazzar.shared.generated.resources.delete_account_warning
import iraniancanadienbazzar.shared.generated.resources.delete_account_wrong_password
import org.jetbrains.compose.resources.stringResource

/**
 * Store-required in-app account deletion (Apple 5.1.1(v) / Play User Data), with password
 * re-authentication for email accounts.
 *
 * Lives in `:shared` rather than `:feature:profile` because it needs [AuthViewModel]. It moved out of
 * `MainScaffold` on 2026-08-02 when Settings became its own pushed route: the sheet has to render
 * over Settings, which is outside the scaffold.
 */
@Composable
fun DeleteAccountSheet(
    authViewModel: AuthViewModel,
    userEmail: String?,
    onDismiss: () -> Unit,
    onDeleted: () -> Unit,
) {
    val deleteState by authViewModel.deleteAccount.collectAsState()
    var password by remember { mutableStateOf("") }
    val needsPassword = userEmail != null
    val dismiss = {
        if (!deleteState.working) {
            onDismiss()
            authViewModel.resetDeleteAccount()
        }
    }
    LaunchedEffect(deleteState.done) {
        if (deleteState.done) {
            onDismiss()
            authViewModel.resetDeleteAccount()
            onDeleted()
        }
    }
    AppBottomSheet(
        onDismissRequest = dismiss,
        title = stringResource(Res.string.delete_account_title),
    ) {
        Text(
            text = stringResource(Res.string.delete_account_warning),
            style = AppTheme.typography.bodyMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        if (needsPassword) {
            AppTextField(
                value = password,
                onValueChange = { password = it },
                label = stringResource(Res.string.delete_account_password_label),
                keyboardType = KeyboardType.Password,
                visualTransformation = PasswordVisualTransformation(),
                modifier = Modifier.fillMaxWidth()
                    .padding(top = AppTheme.spacing.md),
            )
        }
        deleteState.error?.let { error ->
            Text(
                text = stringResource(
                    if (error is AppError.InvalidCredentials) Res.string.delete_account_wrong_password
                    else Res.string.delete_account_failed,
                ),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.error,
                modifier = Modifier.padding(top = AppTheme.spacing.sm),
            )
        }
        AppButton(
            text = stringResource(Res.string.delete_account_confirm),
            onClick = { authViewModel.deleteAccount(password.takeIf { needsPassword }) },
            variant = AppButtonVariant.Destructive,
            loading = deleteState.working,
            leadingIcon = "delete_forever",
            modifier = Modifier.fillMaxWidth()
                .padding(top = AppTheme.spacing.lg),
        )
        AppButton(
            text = stringResource(Res.string.delete_account_cancel),
            onClick = dismiss,
            variant = AppButtonVariant.Secondary,
            modifier = Modifier.fillMaxWidth()
                .padding(top = AppTheme.spacing.sm),
        )
    }
}
